import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { useAuthStore } from '~/stores/auth'

export interface ApiMeta {
  code?: number
  status?: string
  message?: string
}

export interface ApiSession {
  main_token: string
  identity: number | string
  session: number | string
}

export interface ApiResponse<T = any> {
  data: T
  meta?: ApiMeta
  session?: ApiSession
  [key: string]: unknown
}

class ApiClient {
  private axiosInstance: AxiosInstance
  private token: string | null = null
  private defaultToken: string

  constructor(apiURL: string, defaultToken: string) {
    // Store the default token passed from useApi()
    this.defaultToken = defaultToken

    this.axiosInstance = axios.create({
      baseURL: apiURL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Use the defaultToken captured in constructor instead of calling useRuntimeConfig()
        const token = this.token || this.defaultToken

        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response
      },
      async (error) => {
        if (error.response?.status === 401 && error.response?.data?.meta?.message === 'Unauthenticated') {
          // Clear auth state and redirect to login
          if (import.meta.client) {
            const authStore = useAuthStore()
            authStore.logout()
            await navigateTo('/masuk')
          }
        }
        return Promise.reject(error.response.data)
      },
    )
  }

  setToken(token: string | null) {
    this.token = token
  }

  getToken() {
    return this.token
  }

  async get<T = any>(url: string, params?: any): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.get<ApiResponse<T>>(url, { params })
    return response.data
  }

  async getPublic<T = any>(url: string, params?: any): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.get<ApiResponse<T>>(url, { params, headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${this.defaultToken}`,
    } })
    return response.data
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.post<ApiResponse<T>>(url, data, config)
    return response.data
  }

  async put<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.put<ApiResponse<T>>(url, data)
    return response.data
  }

  async delete<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.delete<ApiResponse<T>>(url, { data })
    return response.data
  }

  async postFormData<T = any>(url: string, data: Record<string, any>): Promise<ApiResponse<T>> {
    const formData = new FormData()

    for (const key in data) {
      if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, data[key])
      }
    }

    const response = await this.axiosInstance.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return response.data
  }
}

// Singleton instance
const apiClients: Record<string, ApiClient> = {}

export function useApi(useType: string = 'v1'): ApiClient {
  if (!apiClients[useType]) {
    // Get runtime config within the Nuxt context (in useApi, not in ApiClient constructor)
    const config = useRuntimeConfig()
    const apiURL = useType === 'v2'
      ? String(config.public.apiUrlV2)
      : String(config.public.apiUrlV1)
    const defaultToken = String(config.public.apiTokenDefault)

    // Pass config values to constructor instead of calling useRuntimeConfig() inside
    apiClients[useType] = new ApiClient(apiURL, defaultToken)
  }
  return apiClients[useType]
}
